import { useCallback, useEffect, useRef, useState } from "react";
import { PlayCircle, Search, Loader2, Crown } from "lucide-react";
import { endpoints } from "@/lib/api";
import { ShowCard } from "@/components/ShowCard";
import { AdSlot } from "@/components/AdSlot";
import { LoadError } from "@/components/LoadError";

const PAGE = 24;

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="vod-skeleton">
    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-video rounded-xl bg-[#121620] animate-pulse" />)}
  </div>
);

const toCard = (v) => ({ ...v, thumbnail: v.thumbnailUrl || v.cfThumbnail || v.thumbnail, isPremium: v.access === "premium" });

export default function VodLibrary() {
  const [shows, setShows] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);
  const reqRef = useRef(0);

  const params = useCallback(() => ({ ...(active !== "all" ? { category: active } : {}), ...(query ? { q: query } : {}) }), [active, query]);

  const load = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true); setError(false);
    try {
      const [s, v] = await Promise.all([
        endpoints.shows(PAGE, 0, params()),
        endpoints.vodLibrary(active !== "all" ? active : "", query).catch(() => ({ data: { videos: [] } })),
      ]);
      if (id !== reqRef.current) return;
      const list = Array.isArray(s.data) ? s.data : [];
      setShows(list); setHasMore(list.length === PAGE);
      setUploads((v.data?.videos || []).map(toCard));
    } catch {
      if (id === reqRef.current) { setError(true); setShows([]); }
    } finally { if (id === reqRef.current) setLoading(false); }
  }, [params, active, query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endpoints.categories().then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);

  const loadMore = async () => {
    setMore(true);
    try {
      const { data } = await endpoints.shows(PAGE, shows.length, params());
      const list = Array.isArray(data) ? data : [];
      setShows((p) => { const seen = new Set(p.map((x) => x.id)); return [...p, ...list.filter((x) => !seen.has(x.id))]; });
      setHasMore(list.length === PAGE);
    } catch { /* retry button stays */ } finally { setMore(false); }
  };

  const chip = (on) => `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${on ? "bg-[#E11D2A] text-white" : "bg-[#121620] border border-[#273042] text-slate-200 hover:text-white"}`;
  const empty = !loading && !error && shows.length === 0 && uploads.length === 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
      <div className="flex items-center gap-2 mb-2">
        <PlayCircle className="w-7 h-7 text-[#E11D2A]" aria-hidden="true" />
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Shows & VOD</h1>
      </div>
      <p className="text-slate-300 mb-2">On-demand shows, mixes, interviews and members-only content.</p>
      <p className="text-xs text-slate-400 mb-6 flex items-center gap-1.5" data-testid="vod-premium-note">
        <Crown className="w-3.5 h-3.5 text-[#60A5FA]" aria-hidden="true" />
        Membership unlocks full in-app viewing of every show and Live TV. Premium members also enjoy ad-free viewing.
      </p>

      <AdSlot placement="shows" position="top" className="mb-6" />
      <AdSlot placement="vod" position="top" className="mb-6" />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-6">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Show categories">
          <button onClick={() => setActive("all")} data-testid="vod-cat-all" className={chip(active === "all")}>All</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setActive(c.name)} data-testid={`vod-cat-${c.slug || c.name}`} className={chip(active === c.name)}>{c.name}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vod-search-input" placeholder="Search shows…" aria-label="Search shows"
            className="h-10 pl-9 pr-4 rounded-full bg-[#121620] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]/60 w-full sm:w-64" />
        </div>
      </div>

      {loading ? <Skeleton /> : error ? (
        <LoadError onRetry={load} testid="vod-error" message="We couldn't load shows right now." />
      ) : empty ? (
        <div className="text-center py-20 text-slate-300" data-testid="vod-empty">No shows found{query ? ` for "${query}"` : ""}.</div>
      ) : (
        <>
          {uploads.length > 0 && (
            <section className="mb-10" data-testid="vod-uploads">
              <h2 className="text-white font-bold text-lg mb-4">New videos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {uploads.map((v, i) => <ShowCard key={`v-${v.id}`} show={v} index={i} href={`/videos/${v.id}`} />)}
              </div>
            </section>
          )}
          {shows.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="vod-grid">
              {shows.map((s, i) => <ShowCard key={s.id} show={s} index={i % PAGE} />)}
            </div>
          )}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button onClick={loadMore} disabled={more} data-testid="vod-load-more"
                className="h-11 px-7 rounded-full bg-white/5 border border-[#273042] text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                {more && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />} Load more shows
              </button>
            </div>
          )}
        </>
      )}
      <AdSlot placement="shows" position="bottom" className="mt-8" />
      <AdSlot placement="vod" position="bottom" className="mt-4" />
    </div>
  );
}
